import './setup-surface.css';
import { makeIcon, isIconName, type CaosTreeNode } from '@caos/ui';
import type { OrgMetadata, SetupContributionMeta } from '../metadata-source.js';
import { SETUP_NAV_SEAM_ID } from '../metadata-source.js';
import {
  renderRosterPage,
  defaultRosterFetch,
  placeInConsolePane,
  type RosterPageSpec,
  type RosterFetch,
} from './roster-page.js';
import { renderRosterDetailPage, type RosterDetailPageSpec } from './roster-detail-page.js';
import { renderPermissionSets } from './permission-sets.js';
import { renderSetupSeats } from './setup-seats.js';
import { renderSetupDomains } from './setup-domains.js';
import { renderSetupAccessExplain } from './setup-access-explain.js';
import { renderSetupObjectManager } from './setup-object-manager.js';
import { renderSetupComponentLibrary } from './setup-component-library.js';
import { renderSetupInstalledPackages, INSTALLED_PACKAGES_ROUTE } from './setup-installed-packages.js';
import { renderSeamOrder, SEAM_ORDER_SURFACES } from './seam-order.js';
import { renderPage } from '../content-source.js';
import { orderSeamContributions } from '../seam-order.js';
import { announceOpenRecord } from '../open-record-event.js';
import {
  readAssistantCapabilities,
  renderSetupAssistant,
  runAssistantAction,
  type ControlPlaneFetch,
} from './setup-assistant.js';

/**
 * CAOS-140 / CAOS-141 — the Setup admin app.
 *
 * The Setup package masters the `setup` capability; the ENGINE renders no admin UI, so this host
 * surface paints the Setup console (the CAOS analogue of Salesforce Setup / the original Dragon OS
 * Setup at dev.dragonos.cloudatlantisai.com). It owns every `/setup*` route's content region:
 *   • a persistent left NAV RAIL of setup sections (CAOS-140) — the console navigation, always live;
 *   • Setup HOME — an overview of the sections and which are available now vs coming soon;
 *   • CAOS-785 — OBJECT MANAGER moved OUT of this static list, the same way CAOS-762 moved
 *     Administration out: it is now `caos-pkg-setup`'s own `setup` capability inject claim (an
 *     authored `rosterPage` + `rosterDetailPage`, `setup-object-manager.ts` the thin route-shape
 *     bridge — see that module's doc). Field-Level Security's former STANDALONE top-level page is
 *     retired outright — its editor is reached from Object Manager's own "Fields" table now,
 *     matching the reference app's real IA (Setup → Object Manager → an object → Fields & Relationships).
 *   • CAOS-786 — PAGE LAYOUTS moved out the same way, right behind it: an authored `rosterPage` +
 *     `rosterDetailPage`, `setup-page-layouts.ts` the thin route-shape bridge. Its per-object detail
 *     (each layout's sections, in order, each its own titled table of placed fields) is the first
 *     user of `rosterDetailPage`'s new `groupsAt` REPEATED-table primitive — a section count that
 *     varies per object, expressed declaratively rather than as a hand-drawn per-section loop.
 *   • CAOS-787 — COMPONENT LIBRARY moved out the same way: an authored `rosterPage`
 *     (`component_library_list`, read from the `components` control-plane noun) behind
 *     `setup-component-library.ts`'s thin route-shape bridge. Its per-piece detail stays the
 *     bespoke three-tab working reference CAOS-572 built (`component-reference.ts`) rather than a
 *     generic `rosterDetailPage` record — a piece's live, configured example is not something a
 *     declarative JSON page can express, so that half of the surface is deliberately NOT migrated.
 *   • CAOS-788 — SUBMITTED FEEDBACK moved out the same way: `caos-pkg-setup` authors it as a `page`
 *     (`submitted_feedback`, a controlPlane page on `feedback/list` whose rows carry Approve / Decline /
 *     Convert to ticket, each under its own gate) behind its own `setup` inject claim, drawn by the
 *     generic contributed-page path. CAOS-1497 had kept a host section here because a `rosterPage`
 *     cannot carry per-row actions; a controlPlane `page` can, so this file no longer names it;
 *   • the remaining editors (Value Sets, App Manager, Permission Sets, Permission Set
 *     Groups, Roles, Sharing) — each a real read-only/working browse over its metadata dimension;
 *     their in-place editing is labelled coming-soon where noted. Every Setup section is now real —
 *     none is a dead link.
 *   • CAOS-582 — CONTRIBUTED sections: any installed package's `setup` capability inject claim
 *     (`meta.setupContributions`) draws its own rail group and its own page, generically — this
 *     file never names the package. `SECTION_GROUPS` below is Setup's OWN platform-core sections
 *     only (the sections every workspace has before any admin package installs); `allSectionGroups`/
 *     `allSections` are what the rail and Setup Home actually read, appending every contribution to
 *     that fixed list rather than being the fixed list itself.
 *   • CAOS-762 — the "Administration" group (Users, Permission Sets, Permission Set Groups, Roles,
 *     Sharing, Seat Allocation) used to be baked into `SECTION_GROUPS` as a static array, even
 *     though every one of those screens is `caos-pkg-setup`'s own content, not something the Engine
 *     needs to boot Setup at all. It is now `caos-pkg-setup`'s OWN `setup` capability inject claims
 *     (six, one per screen, `group: 'administration'`) — the SAME `contributedSectionGroups`
 *     mechanism CAOS-582 built for any THIRD-party package, now also carrying the package that
 *     masters `setup`'s own admin content. This file no longer names Users/Roles/Permission Sets/
 *     Permission Set Groups/Sharing/Seat Allocation anywhere in its own section list.
 *
 * Pure DOM + `--caos-*` tokens; the metadata is already fetched by the host (metadata-source), so the
 * console reads it synchronously. Its live reads belong to what declares them: Setup Home's installed
 * packages panel, and each contributed page's own data source.
 */

/** The navigate seam — production passes the host client router; a direct call may omit it. */
export type Navigate = (href: string) => void;

/** A section in the Setup console rail. `available` sections render real content; others are coming soon. */
interface SetupSection {
  readonly key: string;
  readonly label: string;
  readonly blurb: string;
  readonly available: boolean;
  /** The mark this entry leads with, from the shared icon vocabulary (CAOS-324). */
  readonly icon: string;
  /** The Jira story that will build this section (shown on its coming-soon pane) — informational. */
  readonly story?: string;
}

/**
 * The rail groups, in the reference Setup's order and naming: who can do what first, then apps, then
 * the data model, then the platform. The LIST is what this platform genuinely has — the reference
 * also lists things one customer's own application does, which would be dead entries here, and a dead
 * entry is worse than an absent one.
 *
 * CAOS-810 — each group carries the `group` KEY a package's setup claim uses to add to it. The key, not the
 * heading text, is what joins a contribution to one of these groups (see {@link allSectionGroups}).
 */
interface SectionGroup {
  /** The `group` key a setup claim names to add to this group. */
  readonly key: string;
  readonly heading: string;
  readonly sections: readonly SetupSection[];
}

const SECTION_GROUPS: readonly SectionGroup[] = [
  {
    // CAOS-522 — "Nothing edits the theme — branding is the first thing a customer changes."
    // Company settings lead the rail, as they do in the reference.
    //
    // CAOS-790 — Theme & Branding is no longer a section here. It is `caos-pkg-setup`'s own `setup`
    // claim on THIS group key, so it arrives as a contribution and this group no longer declares it:
    // the same move CAOS-789 made for App Manager and CAOS-699 for Object Manager, Page Layouts and
    // Value Sets. The group stays, with its key, its heading and its place at the head of the rail,
    // because that is this file's to own — and a contribution joins a group AHEAD of the group's own
    // sections (see `allSectionGroups`), so Theme & Branding still reads first with Domains after it,
    // exactly as it did when both were declared here.
    //
    // Unlike those three, this group keeps a section of its own (Domains), so it is drawn whether or
    // not the package is installed. In an org without `caos-pkg-setup`, Company now holds Domains
    // alone rather than a Theme & Branding entry whose screen nothing would render.
    key: 'company',
    heading: 'Company',
    sections: [
      {
        // CAOS-78 — a customer's own domain in front of a Site, and the DNS records they must add
        // before it can serve. In Company beside Theme & Branding because it is the same kind of
        // thing: what this org looks like from outside. The steps have to be HERE and not only on
        // the API — every remaining action happens in a DNS zone the platform does not control, so
        // the product's only move is telling one administrator exactly which records to create.
        key: 'domains',
        label: 'Domains',
        blurb: 'Put your own domain in front of a Site, and see what it still needs to go live.',
        available: true,
        // The shared icon vocabulary (CAOS-324) has no globe/world mark, and this ticket does not
        // extend it on a guess — the same call Theme & Branding made when it picked a mark from it.
        // `link` is already the
        // vocabulary's address mark, which is what a domain is.
        icon: 'link',
      },
    ],
  },
  {
    // CAOS-789 — App Manager is `caos-pkg-setup`'s own `setup` claim now, on this group key, so this
    // group has no section of its own. It stays for the reason `objects_fields` below does: the key
    // is what joins a contribution to a place in the rail, and the heading is this file's to own. A
    // group nothing has claimed into is not drawn (see `allSectionGroups`).
    key: 'apps',
    heading: 'Apps',
    sections: [],
  },
  {
    // CAOS-699 — every entry here is a package's own `setup` claim now: Object Manager, Page Layouts
    // and, last, Value Sets, all `caos-pkg-setup`'s. The group stays so that its heading keeps Setup's
    // place in the rail; a group nothing has claimed into is not drawn (see `allSectionGroups`).
    key: 'objects_fields',
    heading: 'Objects & Fields',
    sections: [],
  },
  {
    // CAOS-435 — THE REFERENCE'S FIFTH GROUP, DECLARED HERE FOR THE SAME REASON `apps` AND
    // `objects_fields` ABOVE ARE: the key is what joins a contribution to a place in the rail, and
    // the heading is this file's to own. Nothing in the platform core claims into it, so on its own
    // this entry draws nothing — `allSectionGroups` filters a group with no sections.
    //
    // WHY A CONTRIBUTED `workflow` COULD NOT SUPPLY ITS OWN PLACE, measured rather than assumed,
    // because caos-pkg-setup carried Process Automation in `platform` for two tickets on a reason
    // that was half wrong. A contributed group whose key is NOT one of this file's lands in
    // `separate`, and `separate` is spliced in whole straight after Company — so its position is
    // decided by that splice, not by the group's name. Within the bucket the order falls through to
    // `compareSourceKey`, a plain `<`/`>` on the key, so `workflow` would sort after
    // `administration` rather than ahead of it (the note in that package's suite claimed ahead, and
    // that part was wrong). The rail would read Company, Administration, Workflow, Apps, Objects &
    // Fields, Platform: the right heading two groups too early, ahead of Apps and Objects & Fields
    // instead of between Objects & Fields and Platform.
    //
    // Declared here, the contribution JOINS this group (CAOS-810) and takes this group's place,
    // which is the position the reference gives it. That is the whole purpose of the entry, and it
    // is why this half must be in before the package's claim is regenerated into the registry: this
    // half alone changes no org, and the package's half alone draws in the wrong place.
    //
    // Process Automation is caos-pkg-setup's claim, so there is no section here — and Lifecycles,
    // the reference's other Workflow entry, cannot be authored until the kernel describes
    // lifecycles (CAOS-1757). A group with one claim in it is still the right group.
    key: 'workflow',
    heading: 'Workflow',
    sections: [],
  },
  {
    key: 'platform',
    heading: 'Platform',
    sections: [
      {
        // CAOS-732 built the admin editor for the CAOS-465 seam-order primitive's persisted
        // override, scoped to the most user-visible seam (the right-edge gutter tab stack).
        // CAOS-736 generalized the editor to the kernel-mastered shell seams an administrator has a
        // reason to arrange (`surfaces/seam-order.ts`'s `SEAM_ORDER_SURFACES`, five since CAOS-768
        // added the workspace tab row) — this one rail entry is a HUB
        // (`renderSeamOrderHub` below) listing them, each opening its own `/setup/seam-order/
        // <seamId>` route. Setup's own contributed nav (`setup-nav`) and the app switcher are
        // pinned through a separate mechanism (`contributedSectionGroups` below /
        // `metadata-source.ts`'s app ordering) — see those for why they have no editor here.
        key: 'seam-order',
        label: 'Seam Order',
        blurb: 'Pin the stacking order of the platform seams more than one package can add to.',
        available: true,
        icon: 'list',
      },
      {
        // CAOS-145 — the view over the CAOS-118 `access` verbs: why one person can or cannot reach
        // one record, and which clause decided it. The engine and its API already existed; nothing
        // drew them.
        //
        // IT IS IN PLATFORM AND NOT ADMINISTRATION, which is worth saying because Administration is
        // where an administrator would look first. Administration is `caos-pkg-setup`'s CONTRIBUTED
        // group, and this is a host section, so joining it would mean this file declaring a group
        // keyed `administration` — and CAOS-810 then treats the package's group as JOINING this
        // file's rather than standing on its own, which moves it out of the position CAOS-762 gave
        // it (straight after Company, ahead of every other contributed group) and down among the
        // platform-core groups. Placing one section would silently reorder six. The split is also
        // real rather than a workaround: Administration is where access is CHANGED, and this screen
        // only reports what the resolver concluded.
        key: 'access-explain',
        label: 'Access Explain',
        blurb: 'Ask why a person can or cannot reach one record, and see the clause that decided it.',
        available: true,
        // `eye` — the question this screen answers is what one person can SEE. The access-model
        // family mark (`shield-check`) is already carried by four of `caos-pkg-setup`'s own
        // administration claims, so reusing it here would make the one REPORTING screen
        // indistinguishable in the rail from the four that EDIT.
        icon: 'eye',
      },
      {
        /*
         * CAOS-1736 — Installed Packages is platform-core, so an org always has a way to remove a
         * package. It is in THIS group because that is the group `caos-pkg-setup` already claims it
         * into, so the entry keeps its place in the rail whether it comes from here or from there.
         *
         * This group already held two of its own sections, so unlike Apps and Objects & Fields it
         * was drawn in an org without `caos-pkg-setup` — which is exactly how the defect was
         * visible: the Platform heading was there, with Seam Order and Access Explain under it, and
         * no way to manage packages anywhere in Setup.
         *
         * A contribution claiming this same key is DROPPED rather than joining or superseding this
         * entry — see `allSectionGroups` — so exactly one entry is drawn, and it is the one whose
         * page actually answers the address. An org holding `caos-pkg-setup` sees the same screen it
         * saw before, because the spec here was mirrored from that
         * package's authored page field for field — and since CAOS-1827 a slightly better one, that
         * spec now carrying card facts the authored page never had.
         */
        key: INSTALLED_PACKAGES_ROUTE,
        label: 'Installed Packages',
        blurb:
          'Every package installed in this org, what it brought, and how to remove one.',
        available: true,
        // The same mark `caos-pkg-setup`'s own claim carries, so the entry does not change
        // appearance depending on which of the two provided it.
        icon: 'layers',
      },
    ],
  },
];

/** Setup Home is reached from the rail's own heading, exactly as the reference does — not from a group. */
const HOME_SECTION: SetupSection = {
  key: '',
  label: 'Setup Home',
  blurb: 'Overview of the administration console.',
  available: true,
  // CAOS-378 — a HOUSE, not the gear this carried, so the two records describing Setup's Home agree
  // on its mark. The one a person actually SEES is the Home subtab, which `projectMetadata` mints
  // with `icon: 'house'`; this field is not drawn anywhere today, because every reader of
  // `allSections` skips the empty key (Home is reached from the rail heading, not from a card or a
  // rail entry). It is corrected rather than deleted because it is still the record of what Home is,
  // and the next reader of this list should not have to know which of two answers is the live one.
  icon: 'house',
};

/**
 * CAOS-161 — group-key words that are INITIALISMS rather than words, and so cannot be capitalized
 * by the rule below: `ai` is AI, not "Ai".
 *
 * This exists because a contributed group has no label of its own. A page claim carries
 * `setupNav.label` and a package carries a display label, but the GROUP is only ever the bare key
 * (`group: string` in the claim schema), so {@link humanizeGroupKey} is the sole author of the
 * heading a reader sees — and first-letter capitalization is right for every multi-letter word and
 * wrong for every initialism. Setup's own five groups are all ordinary words, which is why nothing
 * needed this until a package claimed a group named after one.
 *
 * DELIBERATELY ONE ENTRY. The temptation is to pre-fill this with `api`, `sso`, `crm`, `fls` and
 * the rest of the platform's vocabulary, and that would be inventing: an entry here is a claim that
 * some package names a group that word, and nothing does. An initialism costs one line on the day a
 * real claim needs it, and a speculative list reads as a decided convention nobody decided.
 *
 * A VALUE, not a flag, because the correct rendering is not always upper-case — a map can hold
 * `'ios' → 'iOS'` the day something needs it, where a set of "words to upper-case" could not.
 */
const GROUP_KEY_INITIALISMS: ReadonlyMap<string, string> = new Map([['ai', 'AI']]);

/**
 * CAOS-582 — humanize a group key into a rail heading (`'guidance'` → `'Guidance'`,
 * `'sales_ops'` → `'Sales Ops'`). The claim carries only the bare key (schema: `group: string`),
 * so this is the one place that turns it into words, the same way `navStyleLabel` above already
 * humanizes another authored key for display.
 *
 * CAOS-161 — an initialism is looked up rather than capitalized; see {@link GROUP_KEY_INITIALISMS}.
 */
function humanizeGroupKey(key: string): string {
  return key
    .split(/[_-]+/)
    .filter((w) => w.length > 0)
    .map((w) => (GROUP_KEY_INITIALISMS.get(w.toLowerCase()) ?? w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * One page contributed under a group, kept alongside the identity `orderSeamContributions` sorts
 * on (`sourceKey`, `order`) until the group is ordered — the {@link SetupSection} it renders as
 * carries neither.
 */
interface ContributedSection {
  readonly sourceKey: string;
  readonly order?: number;
  readonly section: SetupSection;
}

/**
 * CAOS-582 — the rail groups CONTRIBUTED by installed packages, derived from `meta.setupContributions`
 * (every package's `setup` capability inject claim) rather than any name Setup's own code carries.
 * One heading per distinct `group`, one entry per `page` claimed under it. A claim naming a page
 * this workspace does not hold is skipped — not a dead rail entry, the same fail-soft posture
 * `renderPageSurface`'s own callers already take for a dangling page reference.
 *
 * CAOS-465 — both the GROUPS (headings) and the PAGES within each group are run through
 * `orderSeamContributions` (`../seam-order.js`) rather than kept in the order
 * `meta.setupContributions` happened to list the claims (install order, in every case that
 * matters). A group has no declared order of its own — the claim's `order` field orders pages
 * WITHIN one group, not groups against each other — so a group's sort key is its own group key,
 * with no declared `order`; two groups always settle alphabetically, which is enough to make the
 * arrangement stable without inventing a second ordering concept for groups.
 *
 * CAOS-730 — this reads `meta.setupContributions` (raw org metadata), never
 * `CapabilityRegistry.injectionsFor('setup')`. That is exactly the CAOS-431 finding for
 * `setup-nav` — Setup's own nav rail bypasses the registry entirely — and it is why
 * `RENDERED_INJECTION_POINTS.setup` is `[]` in `kernel/src/capabilities/seam-coverage.ts`.
 * `host/test/seam-render-coverage.test.ts` verifies this structurally (an AST scan proving no
 * `injectionsFor('setup')` call site exists anywhere in `host/src`), not by trusting this comment
 * to stay true — if this function (or anything else in `host/src`) ever starts reading `setup-nav`
 * through the registry, that test fails until `RENDERED_INJECTION_POINTS.setup` is updated to say so.
 *
 * CAOS-736 — the GROUP order is now also PINNABLE, through a wholly SEPARATE read path from the one
 * this function already avoids (`CapabilityRegistry`): `meta.seamOrderOverrides.get(SETUP_NAV_SEAM_ID)`
 * is `caos.seam_order_override`'s row for the `setup-nav` seam, carried onto `OrgMetadata` by
 * `metadata/retrieve` alongside the component bundle (`CapabilityRegistry.seamOrderOverrides()`,
 * `kernel/src/api/metadata-routes.ts`) — never a call through `injectionsFor('setup')`, so the
 * structural invariant above still holds. `orderSeamContributions` itself already treats a pinned
 * group key naming a group nothing currently contributes to as a no-op (it is simply skipped, never
 * thrown) — a stale `setup-nav` pin fails soft the same way a stale edge-anchored pin does, with no
 * extra handling needed here.
 *
 * CAOS-851 — inside a group, two pages can declare the same `order`. From two packages, the package key
 * settles it. From ONE package the package key is the same as well, and with nothing left to compare the
 * sort kept the claims wherever the manifest happened to list them. The entry's own route is now the last
 * key, so the arrangement never depends on where a claim sits. Deploy refuses that tie inside one
 * component, and a route used twice (`kernel/src/capabilities/validate-capabilities.ts` rule 8); this
 * holds the order for two packages that chose the same numbers, and for anything that reached an org
 * before that rule existed.
 */
function contributedSectionGroups(meta: OrgMetadata): readonly SectionGroup[] {
  const byGroup = new Map<string, ContributedSection[]>();
  for (const contribution of meta.setupContributions) {
    const page = meta.pages.get(contribution.page);
    // CAOS-762 — a claim's own `label` stands in when its `page` names no resolvable component
    // (Setup's own Permission Sets section: bespoke host code over a live kernel inspection
    // route, never a `page`/`rosterPage`/`rosterDetailPage` component). Only a claim
    // naming NEITHER a resolvable page NOR its own label has nothing to show — the same fail-soft
    // posture CAOS-582 already established, now covering both ways a claim can supply one.
    const label = contribution.label ?? page?.label;
    if (!label) continue;
    // CAOS-1519 — the package's own display name, when it HAS one. This used to fall back to the
    // raw `sourceKey`, which put a component key straight into the sentence below: a Setup card
    // reading "Added by cr_proving_ground_edge_anchor." Not even humanized — the identifier
    // verbatim, in prose, on the admin console. `undefined` here means the blurb says who added the
    // section only when the platform can actually name them; see the `blurb` line.
    const sourceLabel = meta.labels.get(contribution.sourceKey);
    const list = byGroup.get(contribution.group) ?? [];
    list.push({
      sourceKey: contribution.sourceKey,
      order: contribution.order,
      section: {
        // CAOS-762 — `route` stands in when the rail's URL segment must differ from `page` itself
        // (a route that predates this mechanism, or a section with no backing page component).
        key: contribution.route ?? contribution.page,
        label,
        // CAOS-1519 — an authored blurb wins; otherwise say who added the section, but ONLY when
        // the package has a name to give. Without one the sentence names the package generically
        // rather than reciting its key: the card's own label already says what the section is, so
        // the provenance line is the part that can afford to be vague, and a key in prose is worse
        // than a missing name.
        blurb: contribution.blurb ?? (sourceLabel ? `Added by ${sourceLabel}.` : 'Added by an installed package.'),
        available: true,
        // `layers` is the vocabulary's existing mark for "something else is bundled in here"
        // (CAOS-324's own administration set) — the fallback for a contribution that sets no icon.
        icon: contribution.icon ?? 'layers',
      },
    });
    byGroup.set(contribution.group, list);
  }
  const setupNavPin = meta.seamOrderOverrides.get(SETUP_NAV_SEAM_ID);
  const setupNavOverride = setupNavPin && setupNavPin.length > 0 ? { sourceKeys: setupNavPin } : undefined;
  const orderedGroups = orderSeamContributions(
    [...byGroup.entries()].map(([group, contributions]) => ({ group, contributions })),
    (g) => ({ sourceKey: g.group }),
    setupNavOverride,
  );
  return orderedGroups.map(({ group, contributions }) => ({
    key: group,
    heading: humanizeGroupKey(group),
    sections: orderSeamContributions(contributions, (c) => ({ sourceKey: c.sourceKey, order: c.order, tieBreak: c.section.key })).map(
      (c) => c.section,
    ),
  }));
}

/**
 * CAOS-582 — every rail group Setup draws: its own platform-core groups PLUS whatever installed
 * packages have contributed. This is the seam that makes Setup's nav "built from what apps have
 * contributed, not a fixed list" — a package appears here without this function (or Setup
 * anywhere else) ever naming it.
 *
 * CAOS-762 — contributed groups slot in right after Company, before Apps/Objects & Fields/Platform,
 * rather than trailing after every platform-core group. This is what keeps `caos-pkg-setup`'s own
 * "Administration" contribution (Users, Permission Sets, Permission Set Groups, Roles, Sharing,
 * Seat Allocation) in the SAME position the reference build's Setup nav leads with — "who can do
 * what" straight after company settings, ahead of the data model and the platform sections — now
 * that it draws from a contribution instead of a fixed second array entry. The split point is
 * structural (SECTION_GROUPS's own first entry, Company), not a name this function checks for; any
 * OTHER contributed group lands here too, on the same reasoning Setup's own admin content does —
 * access/administration-shaped content reads first, immediately after company identity.
 *
 * CAOS-810 — a contributed group whose key is one of Setup's own group keys is not a new group. Its pages
 * join that group, in that group's place and under that group's heading. Before this, Object Manager and
 * Page Layouts (`objects_fields`) drew a second heading, "Objects Fields", beside "Objects & Fields", and
 * Component Library and Submitted Feedback (`platform`) drew a second "Platform". Inside a joined group the
 * contributed pages come first in their declared order, then Setup's own: the same place
 * `orderSeamContributions` gives a contribution that declares no order. A `setup-nav` pin on a joined key
 * has nothing to move, because the group's place is Setup's own.
 */
function allSectionGroups(meta: OrgMetadata): readonly SectionGroup[] {
  /*
   * CAOS-1736 — A PLATFORM SECTION KEY IS RESERVED. A contributed claim on one is DROPPED.
   *
   * Nothing could share a key with one of this file's own sections until Installed Packages became
   * platform-core, and `caos-pkg-setup` claims that exact route into that exact group, so the
   * collision arrived with it. Before this rule, both were drawn: two rail entries with the same
   * address, and only one of them reachable, because `renderMain` answers an address once.
   *
   * THE PLATFORM ENTRY WINS, and the reason is that it is the one telling the truth. Every key in
   * {@link SECTION_GROUPS} — `domains`, `seam-order`, `access-explain` and now `installed-packages`
   * — is answered by a branch in `renderMain` that runs BEFORE the contributed-page lookup. So the
   * platform's code renders the screen at that address no matter who claimed it, and drawing a
   * package's label over it would describe the entry as something it is not, filed under whatever
   * heading that package chose.
   *
   * The first version of this had it the other way round: the CONTRIBUTION superseded the
   * platform's own section, so that an org holding `caos-pkg-setup` kept the exact screen it had.
   * A review of #908 showed that trades one defect for two, and both are worse than the duplicate
   * it was fixing:
   *
   *   1. It reopens the very defect this ticket exists to close. A `setup` inject claim needs only
   *      its own `label` to survive `contributedSectionGroups` — its `page` need not resolve to
   *      anything (CAOS-762) — and deploy validation refuses a route collision only WITHIN one
   *      component, never across them (`validate-capabilities.ts` rule 8, which says so in as many
   *      words). So any installed package could claim `installed-packages`, suppress the platform's
   *      entry, intercept the address, and leave the org on "Page not found" with no way to remove
   *      a package. A third-party workspace mastering the shell is exactly the package most likely
   *      to ship a Setup claim, and this ticket is about that org.
   *   2. It was a GLOBAL rule written for ONE collision, so it reached `domains`, `seam-order` and
   *      `access-explain` too. Those three have their own `renderMain` branches above the lookup,
   *      so a colliding claim could not redirect the page — but it still replaced the platform's
   *      rail entry with the package's label, blurb and icon, pointing at a platform screen from
   *      under an unrelated heading.
   *
   * Reserving costs an org holding `caos-pkg-setup` nothing measurable: `INSTALLED_PACKAGES_SPEC`
   * was mirrored from that package's `installed_packages_list.json` field for field (verified
   * against the file, not assumed), so the screen it loses and the screen it gains are the same
   * screen. Since CAOS-1827 the platform's is a superset — it differs only in `key`, `cardFields`
   * and `description`, each annotated there — so the trade is now strictly in the org's favour.
   *
   * Compared across ALL contributed groups rather than only the joined one, because a package is
   * free to claim any group key and could otherwise smuggle the same duplicate in under a heading
   * of its own invention.
   */
  const reserved = new Set(SECTION_GROUPS.flatMap((g) => g.sections.map((s) => s.key)));
  const contributed = contributedSectionGroups(meta)
    .map((g) => ({ ...g, sections: g.sections.filter((s) => !reserved.has(s.key)) }))
    // A contributed group that claimed nothing BUT a reserved key is now empty, and an empty
    // heading is not a destination (CAOS-699). Dropped here so it cannot reach `joined` or
    // `separate` and draw a heading with nothing under it.
    .filter((g) => g.sections.length > 0);
  const joined = new Map(contributed.filter((g) => SECTION_GROUPS.some((own) => own.key === g.key)).map((g) => [g.key, g]));
  const withContributions = (own: SectionGroup): SectionGroup => {
    const added = joined.get(own.key);
    return added ? { ...own, sections: [...added.sections, ...own.sections] } : own;
  };
  const [company, ...platformCore] = SECTION_GROUPS.map(withContributions);
  const separate = contributed.filter((g) => !joined.has(g.key));
  // CAOS-699 — a heading with nothing under it is not a destination. Objects & Fields is drawn only
  // from package claims, so an org without them has no such group rather than an empty one.
  return [...(company ? [company] : []), ...separate, ...platformCore].filter((g) => g.sections.length > 0);
}

/**
 * CAOS-397 — the rail's grouped section list, reshaped from {@link allSectionGroups} into
 * `<caos-tree>` nodes. Recomputed per render (contributions vary by org), the tree component is
 * what turns it into rows, not a loop here building the heading/list/item DOM by hand.
 */
function railNodes(meta: OrgMetadata): CaosTreeNode[] {
  return allSectionGroups(meta).map((group) => ({
    id: group.heading,
    label: group.heading,
    children: group.sections.map((section) => ({
      id: section.key,
      label: section.label,
      icon: section.icon,
      href: `/setup/${section.key}`,
      badge: section.available ? undefined : 'Soon',
    })),
  }));
}

/** All sections, flattened, for lookup by route key — Home, the platform-core groups, and every contribution. */
function allSections(meta: OrgMetadata): readonly SetupSection[] {
  return [HOME_SECTION, ...allSectionGroups(meta).flatMap((g) => g.sections)];
}

/** The route context the console resolves against. */
export interface SetupContext {
  readonly meta: OrgMetadata;
  readonly pathname: string;
  readonly navigate?: Navigate;
  /**
   * CAOS-1809 — who is reading, keyed as a placed component's inputs name them, forwarded to the
   * contributed `page` panes this console draws so a piece declaring `source: "userContext"` is
   * answered here on the same terms as on a tab-bound page. Built by `viewerInputContext`; absent ⇒
   * those inputs are left off the element, which is the no-session state.
   */
  readonly viewer?: Readonly<Record<string, string>>;
}

/**
 * CAOS-324 (S3) — segments that used to name a section and still arrive in links people saved.
 *
 * Setup's route naming was brought to the reference's: the object catalogue is `objects` rather
 * than `object-manager`, the review queue `feedback` rather than `submitted-feedback`. Those names
 * live in the PACKAGE's `setupNav.route`, so the rail, the cards and every link this console draws
 * already emit the new segment. What a rename cannot reach is a URL somebody bookmarked, pasted
 * into a ticket, or wrote into a runbook — and a section that stops resolving does not fail loudly,
 * it renders the "unknown section" pane, which reads as the screen having been removed.
 *
 * THE ALIAS IS A FALLBACK, NOT A REWRITE, and the difference is the whole of its correctness. The
 * host and the package version independently: an org can be running a kernel carrying this file
 * with a pkg_setup that predates the rename, and there the old segment is not legacy at all — it
 * is the live claim. Canonicalising unconditionally would take a working route away from exactly
 * the orgs that had not upgraded yet, which is the opposite of what an alias is for. So the raw
 * segment is tried FIRST against what is actually installed, and the alias is consulted only when
 * nothing claims it.
 *
 * This is the same accommodation the reference makes for its own renames (`/setup/login` still
 * resolves to `login-identity` there). An alias costs one lookup; a dead bookmark costs a person
 * finding out the screen still exists.
 */
const LEGACY_SECTION_ALIASES: ReadonlyMap<string, string> = new Map([
  ['object-manager', 'objects'],
  // CAOS-788 gave the review queue to the package and deliberately kept the host section's own
  // segment so `/setup/submitted-feedback` went on resolving. The reference calls it `feedback`;
  // the claim now does too, and the continuity CAOS-788 preserved is preserved here instead.
  ['submitted-feedback', 'feedback'],
]);

/**
 * The stable IDENTITY of a section, whatever address this org's installed package gives it.
 *
 * The alias map relates two things that a single string used to conflate: the ADDRESS a URL uses,
 * which differs between package versions, and the IDENTITY this file branches on, which does not.
 * Anywhere the host names a specific section — the three routes with their own render branch, the
 * Setup Home count badges — it means the identity, and it must say so: an org still claiming
 * `object-manager` resolves to that key by design (see {@link LEGACY_SECTION_ALIASES}), and a
 * comparison against the literal `objects` would silently miss it. That miss is not cosmetic — it
 * would drop Object Manager into the generic one-segment dispatch and take the nested field editor
 * away from exactly the orgs the alias exists to protect.
 */
function sectionIdentity(key: string): string {
  // One hop, deliberately. No alias VALUE is itself a key today, so a chain cannot occur; if one is
  // ever added, this returns the intermediate rather than the destination and every comparison
  // below quietly stops matching. Resolve the chain when adding it, not by making this recursive —
  // a recursive lookup over a map somebody can make cyclic is a hang, not a fix.
  return LEGACY_SECTION_ALIASES.get(key) ?? key;
}

/**
 * The address THIS org gives an identity — the segment to put in a link the host builds itself.
 *
 * The mirror of {@link sectionIdentity}, and needed for the same reason in the other direction: a
 * link this file writes from a hardcoded identity would 404 on an org whose package still claims
 * the older address. Resolved from what is installed, falling back to the identity when nothing
 * claims it, so an absent section yields a dead link rather than a wrong one.
 */
function sectionAddress(identity: string, meta: OrgMetadata): string {
  return allSections(meta).find((s) => sectionIdentity(s.key) === identity)?.key ?? identity;
}

/**
 * Parse `/setup/<section>/<detail>` into its parts (the leading `setup` segment is fixed).
 *
 * The section key is resolved against the org's installed metadata so a renamed destination keeps
 * answering at its old address — see {@link LEGACY_SECTION_ALIASES} for why that resolution is
 * ordered the way it is. What comes back is an ADDRESS, not an identity: branch on
 * {@link sectionIdentity} of it, never on it directly.
 */
function parseSetupRoute(pathname: string, meta: OrgMetadata): { sectionKey: string; detail?: string } {
  const segments = pathname.split('/').filter(Boolean); // ['setup', <section>, <detail>]
  const raw = segments[1] ?? '';
  const alias = LEGACY_SECTION_ALIASES.get(raw);
  const sectionKey = alias !== undefined && !isKnownSection(raw, meta) ? alias : raw;
  const detail = segments[2];
  return { sectionKey, detail };
}

/** An in-app link that routes through the navigate seam (and keeps a real href for a11y / new-tab). */
function link(text: string, href: string, navigate?: Navigate, className = 'setup-link'): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = className;
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

/** The left nav rail — the console's persistent section navigation (CAOS-140). */
function renderRail(activeKey: string, meta: OrgMetadata, navigate?: Navigate): HTMLElement {
  const rail = document.createElement('nav');
  rail.className = 'setup-rail';
  rail.setAttribute('aria-label', 'Setup');

  // CAOS-592 — the side navigation is ONE placed component: its heading (which doubles as the way
  // back to Setup Home, which is why Home is not an entry in any group), its own Quick Find box,
  // and the section list. Setup drew the first two here and My Profile drew them again; a rail is
  // those pieces together, so they are the component's business now. railNodes(meta) stays the
  // data — Setup's own groups plus whatever packages contributed — and the component renders the
  // groups, headings, rows, "Soon" badges, the filtering, and the empty-result message.
  const tree = document.createElement('caos-tree');
  tree.setAttribute('selected-id', activeKey);
  tree.setAttribute('filterable', '');
  tree.heading = { label: 'Setup', icon: 'settings', href: '/setup' };
  tree.emptyFilterMessage = (term) => `Nothing in Setup matches “${term}”.`;
  tree.nodes = railNodes(meta);
  tree.addEventListener('tree-choose', (ev) => {
    const { href } = (ev as CustomEvent<{ id: string; href?: string }>).detail;
    if (href) navigate?.(href);
  });
  rail.append(tree);
  return rail;
}

/**
 * One glyph from the SHARED icon vocabulary (CAOS-324) — the same marks the components draw, so a
 * Setup entry and a workspace tab for the same thing never disagree. A name nobody recognises draws
 * nothing rather than an empty box.
 */
function glyph(name: string, className: string): HTMLElement {
  const host = document.createElement('span');
  host.className = className;
  host.setAttribute('aria-hidden', 'true');
  if (isIconName(name)) host.append(makeIcon(name));
  return host;
}

/**
 * CAOS-785 — Object Manager’s browse (list + per-object detail, including its "Fields" table and
 * the Field-Level Security link on each row) moved to `setup-object-manager.ts` + authored
 * `rosterPage`/`rosterDetailPage` metadata in `caos-pkg-setup`. `tabsForObject`/`lookupsForObject`/
 * `relatedListsForObject`/`renderObjectList`/`renderObjectDetail`/`isCustom` (the host functions
 * that used to hand-render this) are retired along with it — Object Manager’s own detail no longer
 * shows Relationships/List Views/Tabs sections pending a metadata-expressible replacement for each
 * (tracked as a follow-up rather than re-added as more host code).
 *
 * CAOS-789 — App Manager went the same way, and took the last of this file’s hand-rendering with
 * it. `renderAppList`/`renderAppDetail` and the six helpers that existed only for them — `tag`,
 * `kvRow`, `navStyleLabel`, `isControlPlaneTab`, `tabTargetLabel`, `appNameCell` — are gone, along
 * with the `note` that admitted the screen could be read and not changed. The two pages are
 * `caos-pkg-setup`’s `app_manager_list` and `app_manager_detail`, and nothing names them here:
 * CAOS-1567’s contributed-page dispatch resolves the list from the package’s own claim and the
 * record page from the page that names the list, so App Manager needed no bridge module either.
 */

/**
 * Where you have been in Setup, most recent first. Kept per browser (the reference does the same):
 * it is a convenience, not a record — nothing about it belongs in the org's data.
 */
const RECENTS_KEY = 'caos.setup.recents';
const RECENTS_MAX = 6;

/** Read the recent section keys, tolerating a missing or corrupt store (it is only a convenience). */
function readSetupRecents(): string[] {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : [];
  } catch {
    return [];
  }
}

/** Record a visit to a section. Home is not a destination, so it is not remembered. */
export function pushSetupRecent(key: string): void {
  if (!key) return;
  try {
    const next = [key, ...readSetupRecents().filter((k) => k !== key)].slice(0, RECENTS_MAX);
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* a browser with storage disabled simply has no recents — never a failure the user sees */
  }
}

/** The next things worth configuring, in the order they actually help. */
const RECOMMENDED_NEXT: readonly { readonly key: string; readonly label: string; readonly desc: string }[] = [
  {
    key: 'permission-sets',
    label: 'Decide who can see what',
    desc: 'Grant objects and fields through permission sets, and check a person\u2019s resolved access.',
  },
  {
    key: 'app-manager',
    label: 'Set up your apps',
    desc: 'Arrange each app\u2019s tabs, choose the tab it opens on, and decide who gets it.',
  },
  {
    key: 'objects',
    label: 'Shape your data',
    desc: 'Review each object\u2019s fields, the layout it opens with, and the tabs that surface it.',
  },
];

/**
 * CAOS-763 — the packages installed in this org, read from `packages/list`: the same read the
 * Installed Packages page makes, scoped server-side to this org. Overridable so a test can drive it
 * without a network (mirrors `setContributedPageFetch`).
 */
let setupHomePackagesFetch: RosterFetch = defaultRosterFetch;

/** Override the Setup Home installed-packages fetch seam (tests only). */
/**
 * CAOS-538 — the control-plane fetch the Setup assistant panel uses.
 *
 * Injectable for the same reason the packages fetch above is: a surface test must be able to drive
 * the panel's three states without a network, and the state that matters most is the one where the
 * control plane does not answer.
 */
let setupAssistantFetch: ControlPlaneFetch | undefined;

export function setSetupAssistantFetch(fn: ControlPlaneFetch | undefined): void {
  setupAssistantFetch = fn;
}

export function setSetupHomePackagesFetch(fn: RosterFetch): void {
  setupHomePackagesFetch = fn;
}

/**
 * CAOS-324 (S4) — the deploy's own build, for the System summary. Same seam shape as the packages
 * read above, and injectable for the same reason: the state worth testing is the one where the
 * route does not answer.
 */
let setupHomeVersionFetch: RosterFetch = defaultRosterFetch;

/** Override the Setup Home build read (tests only). */
export function setSetupHomeVersionFetch(fn: RosterFetch): void {
  setupHomeVersionFetch = fn;
}

/**
 * CAOS-403 — the platform version this org targets, from `system/orgVersion`.
 *
 * Its OWN seam rather than a second call through the build seam above, even though both read the
 * `system` noun: they are separate routes with separate failure modes (the build read is
 * unauthenticated and pre-org; this one is org-scoped and gated on `setup.access`), so a test
 * driving one must be able to leave the other exactly as it was.
 */
let setupHomeOrgVersionFetch: RosterFetch = defaultRosterFetch;

/** Override the Setup Home platform-version read (tests only). */
export function setSetupHomeOrgVersionFetch(fn: RosterFetch): void {
  setupHomeOrgVersionFetch = fn;
}

/**
 * The Setup section Installed Packages answers to.
 *
 * CAOS-1736 — this used to be a local constant commented "contributed under (`caos-pkg-setup`'s
 * claim route)", because the page was that package's. It is the platform's now, so the route is
 * declared beside the page (`setup-installed-packages.ts`) and re-exported under the old name here
 * so the Setup Home panel below keeps reading one constant rather than two spellings of a route.
 */
const INSTALLED_PACKAGES_KEY = INSTALLED_PACKAGES_ROUTE;
/** How many packages the panel lists before it points at the full page. */
const INSTALLED_PACKAGES_MAX = 6;

/** One installed package, as `packages/list` returns it. Nothing here says whether it is healthy. */
interface InstalledPackageRow {
  readonly name: string;
  readonly label?: string;
  readonly namespace?: string;
  readonly version?: string;
}

function isInstalledPackageRow(value: unknown): value is InstalledPackageRow {
  if (value == null || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  const optionalString = (v: unknown): boolean => v === undefined || v === null || typeof v === 'string';
  return (
    typeof row.name === 'string' &&
    row.name !== '' &&
    optionalString(row.label) &&
    optionalString(row.namespace) &&
    optionalString(row.version)
  );
}

/**
 * The org's installed packages, or `null` when they could not be read. A failed request, a refused
 * one, and a payload that is not a list of packages are all "could not be read" — never an empty
 * list, because an empty list is a claim that nothing is installed.
 */
async function readInstalledPackages(): Promise<readonly InstalledPackageRow[] | null> {
  let env: { ok?: unknown; data?: { packages?: unknown } | null } | null;
  try {
    const res = await setupHomePackagesFetch('/api/packages/list');
    env = (await res.json()) as typeof env;
  } catch {
    return null;
  }
  const packages = env?.ok === true ? env.data?.packages : undefined;
  if (!Array.isArray(packages) || !packages.every(isInstalledPackageRow)) return null;
  return packages;
}

/**
 * CAOS-324 (S4) — the commit this deployment was built from, for the System summary.
 *
 * THREE OUTCOMES, NOT TWO, and the middle one is why this does not simply return a string. The
 * route answers `buildSha: null` deliberately when the deploy-time variable is unset — "an honest
 * unresolved, not a guess", in its own words — and that is a different fact from the read having
 * failed. Collapsing them would tell an administrator the same thing about a deploy that never
 * recorded its commit and about a summary that could not reach the platform to ask.
 */
async function readBuildSha(): Promise<{ known: boolean; value: string }> {
  let env: { ok?: unknown; data?: { buildSha?: unknown } | null } | null;
  try {
    const res = await setupHomeVersionFetch('/api/system/version');
    env = (await res.json()) as typeof env;
  } catch {
    return { known: false, value: 'Unavailable' };
  }
  if (env?.ok !== true) return { known: false, value: 'Unavailable' };
  const sha = typeof env.data?.buildSha === 'string' ? env.data.buildSha.trim() : '';
  return sha === '' ? { known: false, value: 'Not recorded' } : { known: true, value: sha };
}

/** What the platform-version cell says, and whether what it says is a version. */
export interface PlatformVersionReading {
  /** True only when this org targets a version the platform can name. Drives the `--unknown` class. */
  readonly known: boolean;
  readonly value: string;
  readonly title: string;
}

/**
 * CAOS-403 — turn `system/orgVersion`'s answer into the cell's words.
 *
 * Pure, and exported, because the states here are the point of the cell and there are seven of
 * them: a read that failed, and the six the route distinguishes. Every one of them is a different
 * thing to tell an administrator, and the two that would be easiest to collapse are the two that
 * must not be — an org with no pin recorded has nothing to move up FROM, while an org whose pin
 * matches has nothing to move up TO, and only one of those is a state anybody should act on.
 *
 * The platform's own version leads in every case where it is known, because it is the fact that is
 * true regardless of what the org targets, and the org's target is said beside it rather than
 * instead of it.
 */
export function platformVersionReading(envelope: unknown): PlatformVersionReading {
  const env = envelope as { ok?: unknown; data?: Record<string, unknown> | null } | null;
  const unavailable: PlatformVersionReading = {
    known: false,
    value: 'Unavailable',
    title: 'The platform version could not be read.',
  };
  if (env?.ok !== true || env.data == null || typeof env.data !== 'object') return unavailable;
  const platform = typeof env.data['platformVersion'] === 'string' ? env.data['platformVersion'].trim() : '';
  const targeted = typeof env.data['targetedVersion'] === 'string' ? env.data['targetedVersion'].trim() : '';
  const standing = typeof env.data['standing'] === 'string' ? env.data['standing'] : '';
  // A platform that did not name its own version has not answered this question, whatever else the
  // envelope carried — there is nothing to lead with and nothing to compare a target against.
  if (platform === '') return unavailable;
  switch (standing) {
    case 'current':
      return { known: true, value: platform, title: 'This org targets the platform version it is running on.' };
    case 'behind':
      return {
        known: true,
        value: `${platform} · org targets ${targeted}`,
        title: `This org targets ${targeted}. The platform is at ${platform}, so there is a newer version to move up to.`,
      };
    case 'ahead':
      return {
        known: true,
        value: `${platform} · org targets ${targeted}`,
        title: `This org targets ${targeted}, which is NEWER than the platform serving it (${platform}).`,
      };
    case 'unpinned':
      return {
        known: false,
        value: `${platform} · org not pinned`,
        title: 'This org was provisioned before the platform recorded which version an org targets, so no version is pinned for it.',
      };
    case 'unregistered':
      return {
        known: false,
        value: `${platform} · org not registered`,
        title: 'This org has no registration, so no platform version is recorded for it.',
      };
    case 'unreadable':
      return {
        known: false,
        value: `${platform} · org target unreadable`,
        title: `The version recorded for this org (${targeted}) is not a platform version.`,
      };
    default:
      // A standing this build does not know is not a state to guess at. Reported as unread rather
      // than as one of the six above, for the same reason the build cell refuses to render a
      // missing commit as an empty string.
      return unavailable;
  }
}

/** CAOS-403 — read `system/orgVersion` through its own seam. Never throws; see the cell below. */
async function readPlatformVersion(): Promise<PlatformVersionReading> {
  try {
    const res = await setupHomeOrgVersionFetch('/api/system/orgVersion');
    return platformVersionReading(await res.json());
  } catch {
    return platformVersionReading(null);
  }
}

/**
 * The Installed packages panel. It paints a loading line synchronously and fills in once the read
 * settles, so Setup Home itself never waits on the network.
 */
function renderInstalledPackagesPanel(meta: OrgMetadata, navigate?: Navigate): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'setup-panel setup-panel--packages';
  panel.dataset.state = 'loading';
  const heading = document.createElement('div');
  heading.className = 'setup-panel__heading';
  const headingText = document.createElement('span');
  headingText.textContent = 'Installed packages';
  heading.append(headingText);
  panel.append(heading);

  const status = document.createElement('p');
  status.className = 'setup-panel__empty';
  status.textContent = 'Reading the installed packages\u2026';
  panel.append(status);

  // "View all" only where the page it names is actually in this org's Setup — never a dead link.
  const hasInstalledPackagesPage = allSections(meta).some((s) => s.key === INSTALLED_PACKAGES_KEY);

  // Whatever was painted so far goes, and the panel says the list could not be read.
  const showUnreadable = (): void => {
    panel.dataset.state = 'error';
    status.textContent = 'The installed packages could not be read.';
    heading.querySelector('.setup-panel__count')?.remove();
    panel.replaceChildren(heading, status);
  };

  void readInstalledPackages()
    .then((packages) => {
    if (packages === null) {
      showUnreadable();
      return;
    }
    // The count sits in the heading only once the read has vouched for it.
    const count = document.createElement('span');
    count.className = 'setup-panel__count';
    count.textContent = String(packages.length);
    heading.append(count);

    if (packages.length === 0) {
      panel.dataset.state = 'empty';
      status.textContent = 'No packages are installed in this org yet.';
    } else {
      panel.dataset.state = 'ready';
      const rows = document.createElement('div');
      rows.className = 'setup-panel__rows';
      for (const pkg of packages.slice(0, INSTALLED_PACKAGES_MAX)) {
        const row = document.createElement('div');
        row.className = 'setup-panel__row setup-panel__row--stacked setup-panel__row--static';
        const text = document.createElement('span');
        text.className = 'setup-panel__row-text';
        const label = document.createElement('span');
        label.className = 'setup-panel__row-label';
        label.textContent = pkg.label || pkg.name;
        text.append(label);
        const facts = [pkg.version ? `Version ${pkg.version}` : '', pkg.namespace ? `Namespace ${pkg.namespace}` : ''].filter(
          (f) => f !== '',
        );
        if (facts.length > 0) {
          const desc = document.createElement('span');
          desc.className = 'setup-panel__row-desc';
          desc.textContent = facts.join(' \u00b7 ');
          text.append(desc);
        }
        row.append(text);
        rows.append(row);
      }
      status.replaceWith(rows);
      if (packages.length > INSTALLED_PACKAGES_MAX) {
        const more = document.createElement('p');
        more.className = 'setup-panel__empty';
        more.textContent = `Showing ${INSTALLED_PACKAGES_MAX} of ${packages.length}.`;
        panel.append(more);
      }
    }
    if (hasInstalledPackagesPage) {
      panel.append(link('View all', `/setup/${INSTALLED_PACKAGES_KEY}`, navigate, 'setup-link setup-panel__more'));
    }
    })
    .catch(showUnreadable);

  return panel;
}

/** SETUP HOME — the state of the org, the sections, and where to go next. */
function renderHome(ctx: SetupContext): HTMLElement {
  const { meta, navigate } = ctx;
  const wrap = document.createElement('div');
  wrap.className = 'setup-pane';

  const header = document.createElement('caos-page-header');
  header.setAttribute('eyebrow', 'Administration');
  header.setAttribute('heading', 'Setup Home');
  header.setAttribute('description', 'Administer this org \u2014 access, apps, objects, and platform settings.');
  wrap.append(header);

  // ── the state of the org, across the top ────────────────────────────────
  // Only facts this platform genuinely holds. A number nobody can vouch for is worse than an absent
  // one, so nothing here is inferred or filled in with a placeholder.
  const summary: readonly { readonly label: string; readonly value: string }[] = [
    // CAOS-324 (S4) — the reference's own System summary reports Environment, Build, Active users,
    // Enabled apps, Identity and Other integrations. Of those six this platform holds two: the apps,
    // which were already here under a different name and now carry the reference's, and the BUILD,
    // which became readable when `system/version` shipped and is appended below once it answers.
    //
    // The other four are deliberately not added. Environment and Active users have no route to read
    // them; Identity and Other integrations the reference fills with the hardcoded string "Not
    // connected", and copying a placeholder is not parity — it is exactly the number nobody can
    // vouch for that the note above refuses.
    //
    // The four counts the reference does NOT have stay. Deleting facts this platform genuinely
    // holds, to match a screen that hardcodes two of its own, would make the summary worse in the
    // name of matching it.
    { label: 'Enabled apps', value: String(meta.apps.length) },
    { label: 'Objects', value: String(meta.objects.size) },
    { label: 'Fields', value: String(fieldCount(meta)) },
    { label: 'Value sets', value: String(meta.valueSets.size) },
    { label: 'Saved list views', value: String(listViewCount(meta)) },
  ];
  const summaryPanel = document.createElement('section');
  summaryPanel.className = 'setup-summary';
  const summaryHeading = document.createElement('div');
  summaryHeading.className = 'setup-summary__heading';
  summaryHeading.textContent = 'System summary';
  summaryPanel.append(summaryHeading);
  const summaryGrid = document.createElement('div');
  summaryGrid.className = 'setup-summary__grid';
  for (const s of summary) {
    const cell = document.createElement('div');
    cell.className = 'setup-summary__cell';
    const label = document.createElement('div');
    label.className = 'setup-summary__label';
    label.textContent = s.label;
    const value = document.createElement('div');
    value.className = 'setup-summary__value';
    value.textContent = s.value;
    cell.append(label, value);
    summaryGrid.append(cell);
  }
  // CAOS-324 (S4) — the build is the one cell here that is not already in hand, so the band paints
  // without it and it arrives when the read settles. Setup Home never waits on the network; that is
  // the same rule the Installed packages panel below follows, and it is why the cell is appended
  // rather than reserved: a placeholder that never fills in is a worse answer than a later one.
  const buildCell = document.createElement('div');
  buildCell.className = 'setup-summary__cell setup-summary__cell--build';
  const buildLabel = document.createElement('div');
  buildLabel.className = 'setup-summary__label';
  buildLabel.textContent = 'Build';
  const buildValue = document.createElement('div');
  buildValue.className = 'setup-summary__value';
  buildValue.textContent = '…';
  buildCell.append(buildLabel, buildValue);
  summaryGrid.append(buildCell);
  void (async () => {
    const build = await readBuildSha();
    buildValue.textContent = build.value;
    // The distinction is carried in the DOM as well as in the words, so a screen reader and a
    // stylesheet can both tell a commit from the two things that are not one.
    buildValue.classList.toggle('setup-summary__value--unknown', !build.known);
    buildCell.setAttribute('title', build.known
      ? 'The commit this deployment was built from.'
      : build.value === 'Not recorded'
        ? 'This deploy did not record the commit it was built from.'
        : 'The deployment could not be read.');
  })();

  // CAOS-403 — the build cell says WHICH BUILD is running; this one says which VERSION, and which
  // version this org is judged by. They sit side by side because they are two different facts and
  // neither answers for the other: a commit has no order, no window and no account of what changed
  // between two of them, which is exactly what an administrator asking "what would moving up give
  // us" needs. Appended for the same reason the build is, and on the same rule — Setup Home paints
  // before either read settles and never waits on the network.
  const platformCell = document.createElement('div');
  platformCell.className = 'setup-summary__cell setup-summary__cell--platform-version';
  const platformLabel = document.createElement('div');
  platformLabel.className = 'setup-summary__label';
  platformLabel.textContent = 'Platform version';
  const platformValue = document.createElement('div');
  platformValue.className = 'setup-summary__value';
  platformValue.textContent = '…';
  platformCell.append(platformLabel, platformValue);
  summaryGrid.append(platformCell);
  void (async () => {
    const reading = await readPlatformVersion();
    platformValue.textContent = reading.value;
    platformValue.classList.toggle('setup-summary__value--unknown', !reading.known);
    platformCell.setAttribute('title', reading.title);
  })();

  summaryPanel.append(summaryGrid);
  wrap.append(summaryPanel);

  // ── CAOS-538 — the assistant, in the surface that most needs one ────────────────────────────
  // Mounted in its "not read yet" state and replaced when the kernel answers, so the panel never
  // claims a capability before it has one. `run` performs the action through `ai/act`, which
  // resolves it to a route that already exists and checks this person against THAT route's
  // permission — the outcome is reported verbatim, refusal and all.
  //
  // Each action draws an input for every argument its route accepts, and the values typed are what
  // gets sent. The panel refuses to post an action with a required field still blank, so a button
  // here either does the thing or says which field is missing — it never reports a route's
  // "`label` is required" back as though the person had asked for something impossible.
  const assistantMount = document.createElement('div');
  assistantMount.className = 'setup-assistant-mount';
  assistantMount.append(renderSetupAssistant(null));
  wrap.append(assistantMount);

  void (async () => {
    const caps = await readAssistantCapabilities(setupAssistantFetch);
    const say = (text: string): void => {
      const line = document.createElement('p');
      line.className = 'setup-assistant__outcome';
      line.textContent = text;
      assistantMount.append(line);
    };
    assistantMount.replaceChildren(
      renderSetupAssistant(caps, {
        run: (key, args) => {
          void runAssistantAction(key, args, setupAssistantFetch).then((r) => say(r.message));
        },
      }),
    );
  })();

  // ── the sections, as cards ─────────────────────────────────────
  // A card carries a live count where there is one worth showing, and a chevron where there is not.
  // Nothing is labelled "available" — everything in the rail is.
  const counts = new Map<string, number>([
    ['objects', meta.objects.size],
    ['app-manager', meta.apps.length],
    ['value-sets', meta.valueSets.size],
  ]);
  const grid = document.createElement('div');
  grid.className = 'setup-home-grid';
  for (const section of allSections(meta)) {
    if (section.key === '') continue; // Home is reached from the rail heading, not a card
    const href = `/setup/${section.key}`;
    const card = link('', href, navigate, 'setup-card');
    card.textContent = '';

    const top = document.createElement('div');
    top.className = 'setup-card__top';
    const title = document.createElement('span');
    title.className = 'setup-card__title';
    title.textContent = section.label;
    top.append(title);
    const count = counts.get(sectionIdentity(section.key));
    if (count !== undefined) {
      const countEl = document.createElement('span');
      countEl.className = 'setup-card__count';
      countEl.textContent = String(count);
      top.append(countEl);
    } else {
      top.append(glyph('chevron', 'setup-card__chevron'));
    }

    const blurb = document.createElement('p');
    blurb.className = 'setup-card__blurb';
    blurb.textContent = section.blurb;
    card.append(top, blurb);
    grid.append(card);
  }
  wrap.append(grid);

  // ── where you have been, and where to go next ────────────────────────────
  const panels = document.createElement('div');
  panels.className = 'setup-home-panels';

  const recentPanel = document.createElement('section');
  recentPanel.className = 'setup-panel';
  const recentHeading = document.createElement('div');
  recentHeading.className = 'setup-panel__heading';
  recentHeading.textContent = 'Recent setup items';
  recentPanel.append(recentHeading);
  const sections = allSections(meta);
  const recents = readSetupRecents()
    .map((key) => sections.find((s) => s.key === key))
    .filter((s): s is SetupSection => !!s);
  if (recents.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'setup-panel__empty';
    empty.textContent = 'Sections you open will show up here.';
    recentPanel.append(empty);
  } else {
    const list = document.createElement('div');
    list.className = 'setup-panel__rows';
    for (const section of recents) {
      const row = link('', `/setup/${section.key}`, navigate, 'setup-panel__row');
      row.textContent = '';
      row.append(glyph(section.icon, 'setup-panel__row-glyph'));
      const label = document.createElement('span');
      label.textContent = section.label;
      row.append(label);
      list.append(row);
    }
    recentPanel.append(list);
  }
  panels.append(recentPanel);

  const nextPanel = document.createElement('section');
  nextPanel.className = 'setup-panel';
  const nextHeading = document.createElement('div');
  nextHeading.className = 'setup-panel__heading';
  nextHeading.textContent = 'Recommended next configuration';
  nextPanel.append(nextHeading);
  const nextRows = document.createElement('div');
  nextRows.className = 'setup-panel__rows';
  for (const r of RECOMMENDED_NEXT) {
    const row = link('', `/setup/${sectionAddress(r.key, meta)}`, navigate, 'setup-panel__row setup-panel__row--stacked');
    row.textContent = '';
    const text = document.createElement('span');
    text.className = 'setup-panel__row-text';
    const label = document.createElement('span');
    label.className = 'setup-panel__row-label';
    label.textContent = r.label;
    const desc = document.createElement('span');
    desc.className = 'setup-panel__row-desc';
    desc.textContent = r.desc;
    text.append(label, desc);
    row.append(text, glyph('chevron', 'setup-panel__row-chevron'));
    nextRows.append(row);
  }
  nextPanel.append(nextRows);
  panels.append(nextPanel);

  panels.append(renderInstalledPackagesPanel(meta, navigate));

  wrap.append(panels);
  return wrap;
}

/** How many fields the org's objects carry between them. */
function fieldCount(meta: OrgMetadata): number {
  let n = 0;
  for (const object of meta.objects.values()) n += object.fields.size;
  return n;
}

/** How many saved list views exist across every object. */
function listViewCount(meta: OrgMetadata): number {
  let n = 0;
  for (const views of meta.listViews.values()) n += views.length;
  return n;
}

/** A clearly-labelled coming-soon pane for a section not built this pass — honest, never a dead link. */
function renderComingSoon(section: SetupSection): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'setup-pane';

  const header = document.createElement('caos-page-header');
  header.setAttribute('eyebrow', 'Setup');
  header.setAttribute('heading', section.label);
  wrap.append(header);

  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', 'Coming soon');
  empty.textContent = `${section.blurb} This editor is not built yet.`;
  wrap.append(empty);
  return wrap;
}

/**
 * CAOS-736 — the Seam Order HUB (`/setup/seam-order`): one card per kernel-mastered shell seam
 * this Setup can pin an order for ({@link SEAM_ORDER_SURFACES}), each opening its own
 * `/setup/seam-order/<seamId>` editor. Matches the App Manager / Object Manager list→detail
 * pattern already established in this console, rather than a picker widget bolted onto one page.
 */
function renderSeamOrderHub(ctx: SetupContext): HTMLElement {
  const { navigate } = ctx;
  const wrap = document.createElement('div');
  wrap.className = 'setup-pane';

  const header = document.createElement('caos-page-header');
  header.setAttribute('eyebrow', 'Setup · Platform');
  header.setAttribute('heading', 'Seam Order');
  header.setAttribute(
    'description',
    'Pin the stacking order of the platform seams more than one package can add to. Choose which one to arrange.',
  );
  wrap.append(header);

  const grid = document.createElement('div');
  grid.className = 'setup-home-grid';
  for (const seam of SEAM_ORDER_SURFACES) {
    const href = `/setup/seam-order/${seam.seamId}`;
    const card = link('', href, navigate, 'setup-card');
    card.textContent = '';
    const top = document.createElement('div');
    top.className = 'setup-card__top';
    const title = document.createElement('span');
    title.className = 'setup-card__title';
    title.textContent = seam.heading;
    top.append(title, glyph('chevron', 'setup-card__chevron'));
    const blurb = document.createElement('p');
    blurb.className = 'setup-card__blurb';
    blurb.textContent = `Pin the ${seam.label}.`;
    card.append(top, blurb);
    grid.append(card);
  }
  wrap.append(grid);

  return wrap;
}

/** A loud fallback pane (an unknown object key), never a blank region. */
function renderNotFound(title: string, body: string, navigate?: Navigate): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'setup-pane';
  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', title);
  empty.textContent = body;
  // CAOS-1518 — a dead end needs a way out. Appended AFTER `textContent`, which would otherwise
  // replace it: setting text on an element that already has children drops them.
  //
  // Rendered whether or not a `navigate` seam was supplied. `link` keeps a real `href` and only
  // uses `navigate` to route in-app, so without one this is still a working link — and gating it
  // on the seam would leave the reader with no way back in exactly the contexts that have no
  // router to offer them one.
  //
  // `slot = 'action'` puts it in the component's own dedicated action region rather than trailing
  // inline after the description text — `caos-empty-state` has that slot precisely for "the one
  // thing to do next", which is what this is.
  const back = link('Back to Setup Home', '/setup', navigate);
  back.slot = 'action';
  empty.append(back);
  wrap.append(empty);
  return wrap;
}

/**
 * CAOS-1277 — the component-fetch seam a contributed `rosterPage` resolves through: the SAME
 * `/api/metadata/component-get` round trip every hand-written Setup bridge (`setup-users.ts`,
 * `setup-sharing.ts`, …) already makes for its own hardcoded key, except the key here comes from
 * the CONTRIBUTION, never a string this module names. Overridable so a test can drive it without a
 * network (mirrors every bridge's own `setSetupXFetch`).
 */
const defaultContributedFetch: RosterFetch = (path) =>
  fetch(path, { credentials: 'same-origin' }) as unknown as ReturnType<RosterFetch>;
let contributedFetch: RosterFetch = defaultContributedFetch;

/** Override the contributed-page fetch seam (tests only). */
export function setContributedPageFetch(fn: RosterFetch): void {
  contributedFetch = fn;
}

interface ComponentGetEnvelope {
  ok?: boolean;
  data?: { key?: unknown; body?: Record<string, unknown> } | null;
}

/**
 * GET one authored component by type and key, or `null` on any failure (fail-soft, never a crash).
 *
 * `component-get` answers `{ type, key, body, version }` with the type-specific fields under `body`
 * and `key` NOT repeated inside it, so the key is merged back in for the caller.
 */
async function fetchComponent<T>(type: string, key: string): Promise<T | null> {
  let env: ComponentGetEnvelope;
  try {
    const res = await contributedFetch(
      `/api/metadata/component-get?type=${type}&key=${encodeURIComponent(key)}`,
    );
    env = (await res.json()) as ComponentGetEnvelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  const envelope = env.data;
  if (!envelope || typeof envelope.key !== 'string' || !envelope.body) return null;
  return { ...envelope.body, key: envelope.key } as unknown as T;
}

/**
 * CAOS-1277 — render a package's `setup-nav` contribution GENERICALLY, whichever of the two
 * component shapes it names:
 *
 *   • a `page` resolves synchronously off already-fetched org metadata (`ctx.meta.pages`) exactly
 *     as before — unchanged for Guidance and any other content page.
 *   • a `rosterPage` — the shape EVERY hand-written Setup browse (Users, Sharing, Object Manager,
 *     Page Layouts, Permission Set Groups, Seats, Component Library) already needed a dedicated
 *     host bridge module for, because `ctx.meta.pages` only ever holds `page`-typed components —
 *     now resolves through the SAME `/api/metadata/component-get` fetch those seven bridges already
 *     make by hand, except the key comes from the contribution itself. A new rosterPage-backed
 *     Setup entry needs zero host code.
 *
 * A contribution whose `page` names NEITHER a `page` nor a `rosterPage` component is genuinely
 * unresolvable and SAYS SO (`renderNotFound`) — it never falls through to `renderHome`. A silent
 * redirect to Setup Home reads exactly like a feature that was never installed, which is worse than
 * a page announcing it is missing.
 */
function renderContributedPage(
  contribution: SetupContributionMeta,
  ctx: SetupContext,
  recordId?: string,
): HTMLElement {
  // CAOS-1912 — AN ADDRESSED RECORD OUTRANKS THE LIST'S OWN SHAPE.
  //
  // The `recordId === undefined` guard is the third and last layer of the defect this ticket is
  // about. The `page` lookup used to be unconditional, so `/setup/feedback/f1` found Submitted
  // Feedback in `ctx.meta.pages`, drew the whole triage queue again and discarded the id — the row's
  // own address silently reading as the list it came from. The record branch below was reachable
  // only for a contributed `rosterPage`, because that is the one shape `ctx.meta.pages` does NOT
  // hold, which is why nobody had seen it: the reporter's `my_feedback` list is a rosterPage and
  // worked, and the reviewer's queue is a `page` and could not.
  //
  // `contributedRecord` resolves purely from the list's ADDRESS (`/setup/<route>`) and the record
  // page naming it as `breadcrumbRootHref`, so it never needed to know which shape the list was. An
  // id in the address is a statement about which record is wanted, and it outranks the question of
  // what the thing it came from is. Expressed as a guard here rather than as an early return above,
  // so the one loading pane and the one `contributedRecord` call below stay the only ones.
  const page = recordId === undefined ? ctx.meta.pages.get(contribution.page) : undefined;
  if (page) {
    // CAOS-1764 — a contributed `page` is a PANE of this console like every other, so it is wrapped
    // in `.setup-pane` exactly as the `rosterPage` branch below is. Returning `renderPage`'s surface
    // bare was the edge-to-edge defect: the console owns the whole content region
    // (`.caos-frame__content:has(> .setup-console)` drops the shell's gutter to zero, by design, so
    // the rail can meet the left edge), and `.sa-surface--page` carries no inset of its own — so the
    // ONLY thing standing between a contributed page and the frame edge is this wrapper. Every other
    // Setup surface, hand-written or roster-backed, already had it; a `page` was the one shape that
    // did not, which is why Submitted Feedback sheared against the edge while its neighbours did not.
    const paneForPage = document.createElement('div');
    paneForPage.className = 'setup-pane';
    paneForPage.append(
      renderPage(page, {
        meta: ctx.meta,
        appLabel: ctx.meta.labels.get(contribution.sourceKey) ?? 'Setup',
        // CAOS-1809 — off the surface context, so a contributed Setup page places a piece that
        // declares `source: "userContext"` on the same terms a tab-bound page does.
        ...(ctx.viewer ? { viewer: ctx.viewer } : {}),
      }),
    );
    return paneForPage;
  }

  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  void (async () => {
    if (recordId !== undefined) {
      placeInConsolePane(root, await contributedRecord(contribution, ctx, recordId));
      return;
    }
    const spec = await fetchComponent<RosterPageSpec>('rosterPage', contribution.page);
    if (spec) {
      const { root: surface, done } = renderRosterPage(spec, { navigate: ctx.navigate, fetch: contributedFetch });
      placeInConsolePane(root, surface);
      await done;
      return;
    }
    placeInConsolePane(
      root,
      renderNotFound(
        'Page not found',
        `This section opens a page called "${contribution.page}", and this workspace does not have it. ` +
          'Install the package that provides it.',
      ),
    );
  })();

  return root;
}

/**
 * CAOS-1567 — the record page under a contributed roster, resolved from metadata rather than named
 * in host code.
 *
 * The list's address is `/setup/<route>`, and an addressed `rosterDetailPage` declares that same
 * address as the `breadcrumbRootHref` it walks back to — required by the schema whenever the page
 * takes an id at all. So the page that opens under this list is the one naming this list, and
 * neither component key appears here.
 *
 * Each way of having no answer says which it is, because they call for different fixes: nothing
 * claims the address, or several do and the org would have to be re-authored to say which is meant.
 */
async function contributedRecord(
  contribution: SetupContributionMeta,
  ctx: SetupContext,
  recordId: string,
): Promise<HTMLElement> {
  const listHref = `/setup/${contribution.route ?? contribution.page}`;
  const claimed = ctx.meta.rosterDetailPagesByListHref.get(listHref) ?? [];

  if (claimed.length === 0) {
    return renderNotFound(
      'Page not found',
      `This list has no record page, so there is nothing at "${listHref}/${recordId}". ` +
        'A list opens its rows when the package also ships a record page that names the list.',
    );
  }
  if (claimed.length > 1) {
    return renderNotFound(
      'More than one record page',
      `${claimed.length} record pages name "${listHref}" as the list they belong to, so which one ` +
        'this address opens is not decided. The package that ships them has to say.',
    );
  }

  const spec = await fetchComponent<RosterDetailPageSpec>('rosterDetailPage', claimed[0]!);
  if (!spec) {
    return renderNotFound(
      'Page not found',
      `This section opens a record page called "${claimed[0]}", and this workspace does not have it. ` +
        'Install the package that provides it.',
    );
  }
  const { root: surface, done } = renderRosterDetailPage(spec, recordId, {
    navigate: ctx.navigate,
    fetch: contributedFetch,
    // CAOS-1912 — a piece's component KEY resolved to the element tag its bundle registers, from
    // the org's own installed component list. Read here rather than in the renderer because this is
    // the caller that has org metadata; the other two callers of `renderRosterDetailPage` draw
    // host-named pages that place no pieces, and a page with none never asks.
    //
    // A key the org does not have resolves to `undefined` and the renderer says so in words. That is
    // the right answer rather than a fallback: the component's code is genuinely absent, and
    // inventing an element name from the key would draw an empty box instead.
    componentTag: (key) => ctx.meta.components.find((c) => c.key === key)?.tag,
  });
  await done;
  return surface;
}

/** Does this Setup recognise the route's section at all? (Home is the empty key, and IS one.) */
function isKnownSection(sectionKey: string, meta: OrgMetadata): boolean {
  return allSections(meta).some((s) => s.key === sectionKey);
}

/** Render the main pane for the current Setup route. */
function renderMain(ctx: SetupContext): HTMLElement {
  const { sectionKey, detail } = parseSetupRoute(ctx.pathname, ctx.meta);
  // The branches below name SECTIONS, not addresses — see `sectionIdentity`. `sectionKey` is still
  // what gets handed onward, because that is the key the org's own metadata is filed under.
  const identity = sectionIdentity(sectionKey);

  // CAOS-1567 — Users, Sharing, Roles, Value Sets, Permission Set Groups and Page Layouts used to
  // have a branch here and a bridge module apiece. Every one of them was the same module: read
  // `/setup/<route>[/<id>]`, fetch `<x>_list` or `<x>_detail` by name, hand it to the generic
  // renderer. All six are gone. They fall through to the contributed-page dispatch below, which
  // resolves the list from the claim and the record page from the page that names the list.
  //
  // Three screens are still named here, and each for a reason the generic route does not cover:
  //
  //   • Object Manager — its record address is NESTED (`/setup/objects/<object>/fields/<field>`),
  //     so one `detail` segment does not describe it. The dispatch reads one segment.
  //   • Seats — a SINGLETON record page (CAOS-557): no id, no list, and so no list address to be
  //     bound to. Its route has no record segment to resolve.
  //   • Component Library — its record view is composed from a second source rather than being one
  //     authored `rosterDetailPage`.
  //
  // None of the three is blocked by anything here; each needs its own shape described before it can
  // be authored, which is more than this ticket is.

  if (identity === 'domains') {
    // CAOS-78 — a thin route-shape bridge over the four `sites` verbs, the same shape
    // `seam-order` is. The whole path is passed rather than one `detail` segment because the
    // trailing segment is a Site KEY, which may itself contain characters the single-segment
    // parse would not survive.
    return renderSetupDomains({ pathname: ctx.pathname, navigate: ctx.navigate }).root;
  }

  if (identity === 'objects') {
    // CAOS-785 — authored metadata (a `rosterPage` + `rosterDetailPage` in caos-pkg-setup) behind
    // the generic renderers, the same shape `setup-sharing.ts` already established; the nested
    // field detail route needs the FULL remaining path, not just one `detail` segment, so this
    // module parses `ctx.pathname` itself rather than reusing `parseSetupRoute`'s single `detail`.
    return renderSetupObjectManager({
      meta: ctx.meta,
      pathname: ctx.pathname,
      navigate: ctx.navigate,
      // CAOS-1809 — forwarded, so the field editor's placed piece is told who is reading it.
      ...(ctx.viewer ? { viewer: ctx.viewer } : {}),
    }).root;
  }

  /**
   * CAOS-395 — the BROWSE falls through to the authored page; the DETAIL stays here.
   *
   * `caos-pkg-setup` ships `page:permission_sets` (a `setup` surface over a `list` dataSource on
   * the `permission_set` system object) and has since caos-pkg-setup#106. It drew nothing, because
   * this dispatch matched on `identity` alone and the compiled surface won: the authored page
   * installed, validated, and was shadowed. Narrowing to `detail` is what makes it draw, and it is
   * the first observable proof that the system-object conversion works at all.
   *
   * THE DETAIL IS NOT A MIGRATION THAT HAS NOT HAPPENED YET — it is four screens with no metadata
   * home, measured rather than assumed. `permission-sets.ts` renders, past the list: the object
   * CRUD matrix and the field-grant table (`renderSetDetail`), the assignees list
   * (`renderAssignees`), and a permission set GROUP's own detail including its members
   * (`renderGroupDetail`, reached through this same route because a `psg_*` key comes back
   * `kind: 'group'` from `permissions/get`). The authored record page carries exactly three
   * fields — `label`, `api_name`, `session_required`. A set's object and field grants are
   * variable-length lists nested inside the `permissionSet` component body, so they are related
   * lists over child records, and CAOS-468's dotted `sourcePath` reaches ONE value, not a list.
   * `permissionSetGroup` is a different object this ticket never scoped.
   *
   * So letting the detail fall through too would draw three fields where four screens were and
   * answer a group key with a not-found. Representing a set's grants as records is separate work
   * and should be decided on its own terms rather than inside this ticket's deletion step.
   *
   * This is the mirror image of the `installed_packages_list` narrowing above: there the LIST is
   * reserved to the platform and the record falls through; here the list falls through and the
   * record is kept. Both exist because one route serves two screens with different owners.
   */
  if (identity === 'permission-sets' && detail) {
    return renderPermissionSets({ pathname: ctx.pathname, navigate: ctx.navigate }).root;
  }

  if (identity === 'seats') {
    // CAOS-557 — authored metadata (one SINGLETON `rosterDetailPage` component in caos-pkg-setup),
    // interpreted by the shared, app-agnostic renderer; this call site only names the route.
    return renderSetupSeats({ navigate: ctx.navigate }).root;
  }

  if (identity === 'component-library') {
    // CAOS-714/CAOS-787 — authored metadata (a `rosterPage` + the `components` control-plane
    // noun) behind `setup-component-library.ts`'s thin route-shape bridge, the same shape
    // CAOS-785/CAOS-786 established for Object Manager/Page Layouts; this call site only names
    // the route. Its nav rail entry is now `caos-pkg-setup`'s own `setup` capability inject claim
    // (group `platform`), not this file's hardcoded `SECTION_GROUPS`.
    return renderSetupComponentLibrary({ pathname: ctx.pathname, navigate: ctx.navigate }).root;
  }

  if (identity === 'access-explain') {
    // CAOS-145 — a thin route-shape bridge over the two `access` verbs, the same shape `domains`
    // and `seam-order` are. No `detail` segment: the question is asked in the form, not in the
    // address, because a record id in a URL would be a link that only works for whoever can
    // already see that record.
    return renderSetupAccessExplain().root;
  }

  /*
   * CAOS-1736 — the platform's own Installed Packages page, so an org always has a way to remove a
   * package even when nothing installed provides a screen for it.
   *
   * ABOVE THE CONTRIBUTED-PAGE LOOKUP, with the other platform-core routes, and that position is
   * the mechanism rather than a detail of it. The first version of this sat BELOW the lookup so that
   * `caos-pkg-setup`'s authored page would answer first in an org holding it. A review of #908
   * showed what that costs: a `setup` inject claim needs only its own `label` to be drawn and its
   * `page` need not resolve to anything (CAOS-762), and deploy refuses a route collision only within
   * one component and never across them (`validate-capabilities.ts` rule 8). So any installed
   * package could claim this route and intercept it, and an org whose workspace shipped such a
   * claim would land on "Page not found" with no way to remove a package — the exact defect this
   * ticket exists to close, reachable by the exact kind of package it is about.
   *
   * Answering first closes that by construction rather than by trusting a publisher. `caos-pkg-setup`
   * loses nothing measurable: {@link INSTALLED_PACKAGES_SPEC} was mirrored from its
   * `installed_packages_list` field for field, so the screen that stops being drawn and the screen
   * that replaces it are the same screen — and since CAOS-1827 a superset of it, not a copy. Its rail claim is dropped as a reserved key in `allSectionGroups`, which is the
   * other half of this and is what keeps the entry and the page agreeing about who answers.
   */
  /*
   * THE LIST ADDRESS ONLY — `/setup/installed-packages`, never `/setup/installed-packages/<id>`.
   *
   * The record screen is a `rosterDetailPage` the package ships (`installed_packages_detail`), and
   * the platform declares no equivalent: {@link INSTALLED_PACKAGES_SPEC} is one roster, and a record
   * page is a second component with its own fields. Answering the record address here would hand it
   * a spec that cannot draw it — the detail segment would be dropped and the LIST would be drawn
   * under a record's URL, which is the shape of wrong answer that reads as a right one.
   *
   * Caught by `setup-package-shipped.test.ts`, which walks every editor `caos-pkg-setup` ships and
   * asserts each one opens its own screen. Both of its claims about this page failed when this
   * branch was unconditional, and they were right to: the list stopped asking for the authored
   * component at all, and the record screen stopped existing.
   *
   * So the two addresses divide by what each actually needs. The LIST is the one this ticket is
   * about — being able to see and remove a package — and it is the one no package may intercept.
   * The RECORD screen stays a package capability, reached through the contributed lookup below, and
   * is a not-found in an org that ships no such component. That is the honest answer there: the
   * platform has no record screen to offer, and saying so beats drawing the list again.
   */
  if (identity === INSTALLED_PACKAGES_ROUTE && !detail) {
    return renderSetupInstalledPackages({ navigate: ctx.navigate }).root;
  }

  if (identity === 'seam-order') {
    if (!detail) return renderSeamOrderHub(ctx);
    const config = SEAM_ORDER_SURFACES.find((s) => s.seamId === detail);
    if (!config) {
      return renderNotFound('Seam not found', `"${detail}" is not one of the seams this Setup can order.`);
    }
    return renderSeamOrder({ navigate: ctx.navigate, seamId: detail }).root;
  }

  // CAOS-582 — a package's declared `setup` capability inject claim: render the page it names,
  // through the SAME generic page renderer a tab's page target already uses (content-source.ts) —
  // no per-app branch, so any package that declares this appears exactly the way Guidance does.
  // CAOS-1277 — matched against the RAIL's own key (`route ?? page`, the same fallback
  // `contributedSectionGroups` already uses to build the link `sectionKey` is parsed from), not
  // `page` alone. A claim whose `route` differs from `page` (CAOS-762) used to miss this lookup
  // entirely — the URL segment is the route, so comparing it to `page` could never match — and fall
  // through, past the `allSections` check below, all the way to `renderHome` with no error of any
  // kind. That silent fall-through is what made an unresolvable Setup entry indistinguishable from
  // one that was never installed; see `renderContributedPage` for the other half of the fix.
  const contribution = ctx.meta.setupContributions.find((c) => (c.route ?? c.page) === sectionKey);
  if (contribution) {
    // CAOS-1567 — `/setup/<route>/<id>` opens the record page that names this list, not the list again.
    return renderContributedPage(contribution, ctx, detail);
  }

  const section = allSections(ctx.meta).find((s) => s.key === sectionKey);
  if (section && !section.available) return renderComingSoon(section);

  // CAOS-1518 — an address nothing claims is a NOT-FOUND, not the overview.
  //
  // This fall-through used to end at `renderHome`, so `/setup/<anything>` landed on the overview
  // with nothing to say the address was wrong. A stale bookmark, a typo, and a link to a screen a
  // package has not installed all looked identical to deliberately opening Home — which is how a
  // missing screen gets read as a working one. CAOS-1277 fixed a specific case of this (a
  // contribution whose `route` differs from its `page` missing its lookup and falling through
  // here); the general fall-through is this one, and it is the same defect for every other cause.
  //
  // Only a section this Setup does not recognise AT ALL reaches this. The two states that are not
  // "unknown" are answered above and keep their own screens: a registered-but-unavailable section
  // is `renderComingSoon`, and a package's contributed page is `renderContributedPage`. Home is the
  // EMPTY section key (`HOME_SECTION`), so `/setup` itself matches `section` and is never a 404.
  if (!section) {
    return renderNotFound(
      'That Setup page does not exist',
      `Nothing in this organization's Setup answers to "${ctx.pathname}". The address may be ` +
        'mistyped, or it may belong to a screen a package has not installed here.',
      ctx.navigate,
    );
  }

  // Home (the empty section key), or a registered section whose pane is drawn by the overview.
  return renderHome(ctx);
}

/**
 * Render the Setup console: the persistent left nav rail + the resolved main pane for the route.
 * Handed to the shell frame as the `/setup*` content region (the frame appends it verbatim).
 */
export function renderSetupSurface(ctx: SetupContext): HTMLElement {
  const console_ = document.createElement('div');
  console_.className = 'setup-console';
  const { sectionKey } = parseSetupRoute(ctx.pathname, ctx.meta);
  const known = isKnownSection(sectionKey, ctx.meta);
  // CAOS-324 — remember where you have been, so Setup Home can offer it back. An address nothing
  // claims is not somewhere you have been: the empty key is `pushSetupRecent`'s own "not a
  // destination" value, so an unknown route records nothing rather than a dead link.
  pushSetupRecent(known ? sectionKey : '');
  // CAOS-695 — a Setup page you open earns its own subtab beside Home, the way a record does (Dragon
  // OS parity, UI-565): ONE entry per section, labelled as the rail labels it, following you to
  // wherever inside the section you are, and closing back to Setup Home. Home is the base tab, not an
  // entry, and an address nothing claims opens nothing.
  const section = known && sectionKey !== '' ? allSections(ctx.meta).find((s) => s.key === sectionKey) : undefined;
  if (section) {
    announceOpenRecord({ id: `/setup/${section.key}`, href: ctx.pathname, label: section.label, closeTo: '/setup' });
  }
  // CAOS-1518 — the rail gets the route's OWN key, which simply matches no entry when the section
  // is unrecognised. It used to be collapsed to Home's empty key, which marked Setup Home
  // `aria-current="page"` while the pane said the page does not exist — the rail and the pane
  // disagreeing about where you are, and a screen reader announcing "current page: Setup" over a
  // not-found. Harmless while the pane WAS Home; a contradiction now that it is not.
  console_.append(renderRail(sectionKey, ctx.meta, ctx.navigate), renderMain(ctx));
  return console_;
}
